import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import C from "@constants/constants";

import usePrevious from "@hooks/usePrevious";

const isDevelopmentEnvironment = C.ENVIRONMENT === "local" || C.ENVIRONMENT === "staging";

const pathToTitleMap: { [key: string]: string } = {
  "/campaigns/create/account": "Create Campaign Account",
  "/campaigns/create/compose": "Create Campaign Compose",
  "/campaigns/create/confirmation": "Campaign Sent Confirmation",
  "/campaigns/create/personalize": "Create Campaign Personalize",
  "/campaigns/create/review": "Create Campaign Review",
  "/campaigns/create/setup": "Create Campaign Setup",
  "/login": "Log In",
  "/organization": "Organization Portfolio",
  "/password-reset": "Reset Your ScaleIp Password",
  "/portfolio": "Portfolio",
  "/portfolio/technology/campaigns": "Technology Campaigns",
  "/portfolio/technology/companies": "Technology Companies",
  "/portfolio/technology/contact-results": "Technology Contact Results",
  "/portfolio/technology/contacts": "Technology Contacts",
  "/portfolio/technology/details": "Technology Details",
  "/portfolio/technology/marketing-summary": "Technology Marketing Summary",
  "/portfolio/technology/overview": "Technology Overview",
  "/signup": "Sign Up",
  "/smart-search/companies": "Index Search",
  "/smart-search/contacts": "Index Contacts",
};

const dynamicTechnologyPathRegex = (segment: string) => new RegExp(`^/portfolio/technology/[a-zA-Z0-9-]+/${segment}$`);
const dynamicCampaignPathRegex = (segment: string) => new RegExp(`^/campaigns/create/[a-zA-Z0-9-]+/${segment}$`);
const dynamicOrganizationPathRegex = () => new RegExp(`^/organization/[a-zA-Z0-9-]+$`);
const dynamicCampaignUuidPathRegex = (segment: string) => new RegExp(`^/campaigns/[a-zA-Z0-9-]+/${segment}$`);

const dynamicPaths: { [key: string]: RegExp } = {
  "/campaigns/contacts": dynamicCampaignUuidPathRegex("contacts"),
  "/campaigns/create/account": dynamicCampaignPathRegex("account"),
  "/campaigns/create/compose": dynamicCampaignPathRegex("compose"),
  "/campaigns/create/confirmation": dynamicCampaignPathRegex("confirmation"),
  "/campaigns/create/personalize": dynamicCampaignPathRegex("personalize"),
  "/campaigns/create/review": dynamicCampaignPathRegex("review"),
  "/campaigns/create/setup": dynamicCampaignPathRegex("setup"),
  "/campaigns/performance": dynamicCampaignUuidPathRegex("performance"),
  "/organization": dynamicOrganizationPathRegex(),
  "/portfolio/technology/campaigns": dynamicTechnologyPathRegex("campaigns"),
  "/portfolio/technology/companies": dynamicTechnologyPathRegex("companies"),
  "/portfolio/technology/contact-results": dynamicTechnologyPathRegex("contact-results"),
  "/portfolio/technology/contacts": dynamicTechnologyPathRegex("contacts"),
  "/portfolio/technology/details": dynamicTechnologyPathRegex("details"),
  "/portfolio/technology/marketing-summary": dynamicTechnologyPathRegex("marketing-summary"),
  "/portfolio/technology/overview": dynamicTechnologyPathRegex("overview"),
};

export function usePageView() {
  const location = useLocation();
  const previousLocation = usePrevious(location);

  useEffect(() => {
    if (isDevelopmentEnvironment) {
      return;
    }

    let name = location.pathname;
    for (const [path, regex] of Object.entries(dynamicPaths)) {
      if (regex.test(name)) {
        name = path;
      }
    }

    if (previousLocation && previousLocation.pathname === name) return;
    if (Object.prototype.hasOwnProperty.call(pathToTitleMap, name)) {
      const properties = {
        title: pathToTitleMap[name],
        url: globalThis.location.href,
      };
      // @ts-expect-error TODO: Why is this throwing a typescript error?
      globalThis.analytics.page("App", name, properties);
    }
  }, [location, previousLocation]);
}
